// Scale label definitions based on question type and Excel file specifications

export interface ScaleOption {
  label: string;
  value: string;
  numericValue: number;
}

/**
 * Detects the scale type from question answerOptions and scoringRules and returns appropriate labels
 */
export function detectScaleType(answerOptions: any, scoringRules?: any): 'never-always' | 'never-regularly' | 'agree-disagree' | 'strongly-agree-disagree' | 'none-all-partners' | 'none-all-partners-disclose' | 'none-all' | 'numeric' | 'custom' {
  if (!answerOptions) return 'never-always'; // Default

  const optionsStr = typeof answerOptions === 'string' 
    ? answerOptions 
    : JSON.stringify(answerOptions).toLowerCase();

  const scoringStr = scoringRules ? (typeof scoringRules === 'string' ? scoringRules : JSON.stringify(scoringRules).toLowerCase()) : '';

  // Check for specific scale descriptions in the text
  if (optionsStr.includes('agency partners') || optionsStr.includes('no agency partners')) {
    if (optionsStr.includes('disclose') || scoringStr.includes('disclose use')) {
      return 'none-all-partners-disclose';
    }
    return 'none-all-partners';
  }

  if (optionsStr.includes('no') && optionsStr.includes('all') && !optionsStr.includes('agency')) {
    return 'none-all';
  }

  if (optionsStr.includes('agree') && optionsStr.includes('disagree')) {
    if (optionsStr.includes('strongly')) {
      return 'strongly-agree-disagree';
    }
    return 'agree-disagree';
  }
  
  // Check for Regularly scales (Never, Sometimes, Regularly)
  if (optionsStr.includes('regularly')) {
    return 'never-regularly';
  }
  
  if (optionsStr.includes('never') && optionsStr.includes('always')) {
    return 'never-always';
  }
  
  if (optionsStr.match(/\d+/)) {
    return 'numeric';
  }

  return 'custom';
}

/**
 * Gets scale labels based on detected type
 */
export function getScaleLabels(answerOptions: any, scoringRules?: any): ScaleOption[] {
  // Normalize answerOptions - handle arrays and objects that might be passed
  // For scale questions, answerOptions should be a string description, but we need to handle edge cases
  let normalizedAnswerOptions = answerOptions;
  if (Array.isArray(answerOptions)) {
    // If it's an array, try to join it or use first element
    normalizedAnswerOptions = answerOptions.length > 0 ? answerOptions.join(', ') : '';
  } else if (answerOptions && typeof answerOptions === 'object' && 'options' in answerOptions) {
    // If it's an object with 'options' property, extract the options
    const opts = (answerOptions as { options: string[] }).options;
    normalizedAnswerOptions = Array.isArray(opts) ? opts.join(', ') : JSON.stringify(answerOptions);
  } else if (answerOptions && typeof answerOptions !== 'string') {
    // For any other object type, stringify it
    normalizedAnswerOptions = JSON.stringify(answerOptions);
  }
  
  const scaleType = detectScaleType(normalizedAnswerOptions, scoringRules);
  
  // For Never-Always scales, check if it's inverted based on scoring rules
  let isInvertedNeverAlways = false;
  if (scaleType === 'never-always' && scoringRules) {
    const scoringStr = typeof scoringRules === 'string' ? scoringRules : JSON.stringify(scoringRules);
    // Check if Never = 4 (inverted) or Never = 0 (normal)
    if (scoringStr.match(/never\s*=\s*4/i) || scoringStr.match(/always\s*=\s*0/i)) {
      isInvertedNeverAlways = true;
    }
  }
  
  // Use normalized answerOptions for all processing
  const answerOptionsForProcessing = normalizedAnswerOptions;
  
  switch (scaleType) {
    case 'agree-disagree':
      // Agree = 4, Somewhat Agree = 3, Neutral = 2, Somewhat Disagree = 1, Australian Disagree = 0
      return [
        { label: 'Agree', value: 'Agree', numericValue: 4 },
        { label: 'Somewhat Agree', value: 'Somewhat Agree', numericValue: 3 },
        { label: 'Neutral', value: 'Neutral', numericValue: 2 },
        { label: 'Somewhat Disagree', value: 'Somewhat Disagree', numericValue: 1 },
        { label: 'Disagree', value: 'Disagree', numericValue: 0 }
      ];

    case 'strongly-agree-disagree':
      // Strongly Agree = 4, Agree = 3, Neutral = 2, Disagree = 1, Strongly Disagree = 0
      return [
        { label: 'Strongly Agree', value: 'Strongly Agree', numericValue: 4 },
        { label: 'Agree', value: 'Agree', numericValue: 3 },
        { label: 'Neutral', value: 'Neutral', numericValue: 2 },
        { label: 'Disagree', value: 'Disagree', numericValue: 1 },
        { label: 'Strongly Disagree', value: 'Strongly Disagree', numericValue: 0 }
      ];

    case 'none-all-partners':
      // Scale: No Agency Partners to All Agency Partners
      // No Agency Partners = 0, All = 4
      return [
        { label: 'All', value: 'All', numericValue: 4 },
        { label: 'Between Some & All', value: 'Between Some & All', numericValue: 3 },
        { label: 'Some', value: 'Some', numericValue: 2 },
        { label: 'Between None & Some', value: 'Between None & Some', numericValue: 1 },
        { label: 'No Agency Partners', value: 'No Agency Partners', numericValue: 0 }
      ];

    case 'none-all':
      // Generic None/No to All scale
      return [
        { label: 'All', value: 'All', numericValue: 4 },
        { label: 'Between Some & All', value: 'Between Some & All', numericValue: 3 },
        { label: 'Some', value: 'Some', numericValue: 2 },
        { label: 'Between None & Some', value: 'Between None & Some', numericValue: 1 },
        { label: 'None', value: 'None', numericValue: 0 }
      ];

    case 'never-always':
      // Check if inverted (Never = 4) or normal (Never = 0)
      // Inverted is for negative behaviors (exceptions, unused content)
      // Normal is for positive behaviors (collaboration, good practices)
      if (isInvertedNeverAlways) {
        // Inverted: Never = 4 (best - no exceptions), Always = 0 (worst - always exceptions)
        return [
          { label: 'Never', value: 'Never', numericValue: 4 },
          { label: 'Between Never & Sometimes', value: 'Between Never & Sometimes', numericValue: 3 },
          { label: 'Sometimes', value: 'Sometimes', numericValue: 2 },
          { label: 'Between Sometimes & Always', value: 'Between Sometimes & Always', numericValue: 1 },
          { label: 'Always', value: 'Always', numericValue: 0 }
        ];
      } else {
        // Normal: Never = 0 (worst - never happens), Always = 4 (best - always happens)
        return [
          { label: 'Always', value: 'Always', numericValue: 4 },
          { label: 'Between Sometimes & Always', value: 'Between Sometimes & Always', numericValue: 3 },
          { label: 'Sometimes', value: 'Sometimes', numericValue: 2 },
          { label: 'Between Never & Sometimes', value: 'Between Never & Sometimes', numericValue: 1 },
          { label: 'Never', value: 'Never', numericValue: 0 }
        ];
      }

    case 'never-regularly':
      // Scale: Never, Sometimes, Regularly (or sometimes Always at the end instead of Regularly)
      // Never = 0, Between Never & Sometimes = 1, Sometimes = 2, Between Sometimes & Regularly = 3, Regularly/Always = 4
      // Check scoring rules to see if it uses "Always" or "Regularly" at the end
      const lastOption = scoringRules && typeof scoringRules === 'string' && scoringRules.includes('Always = 4')
        ? 'Always'
        : 'Regularly';
      return [
        { label: lastOption, value: lastOption, numericValue: 4 },
        { label: 'Between Sometimes & Regularly', value: 'Between Sometimes & Regularly', numericValue: 3 },
        { label: 'Sometimes', value: 'Sometimes', numericValue: 2 },
        { label: 'Between Never & Sometimes', value: 'Between Never & Sometimes', numericValue: 1 },
        { label: 'Never', value: 'Never', numericValue: 0 }
      ];

    case 'none-all-partners-disclose':
      // Variant: "No Agency Partners Disclose Use" to "All Agency Partners Disclose Use"
      return [
        { label: 'All Agency Partners Disclose Use', value: 'All Agency Partners Disclose Use', numericValue: 4 },
        { label: 'Between Some & All', value: 'Between Some & All', numericValue: 3 },
        { label: 'Some', value: 'Some', numericValue: 2 },
        { label: 'Between None & Some', value: 'Between None & Some', numericValue: 1 },
        { label: 'No Agency Partners Disclose Use', value: 'No Agency Partners Disclose Use', numericValue: 0 }
      ];

    case 'numeric':
      // Extract numeric range
      // answerOptionsForProcessing is already normalized to string
      const numericMatch = answerOptionsForProcessing?.match(/(\d+).*?(\d+)/);
      if (numericMatch) {
        const start = parseInt(numericMatch[1]);
        const end = parseInt(numericMatch[2]);
        const options: ScaleOption[] = [];
        for (let i = start; i <= end; i++) {
          options.push({
            label: i.toString(),
            value: i.toString(),
            numericValue: i
          });
        }
        return options;
      }
      return [];

    case 'custom':
      // First, try to extract exact labels from scoring rules if available
      // This is the most reliable source for custom descriptive scales
      if (scoringRules) {
        const scoringStr = typeof scoringRules === 'string' ? scoringRules : JSON.stringify(scoringRules);
        // Parse patterns like "Label = 0", "Label = 1", etc.
        const labelPattern = /([^=\n\r]+?)\s*=\s*(\d+)/g;
        const matches: Array<{label: string, value: number}> = [];
        let match;
        while ((match = labelPattern.exec(scoringStr)) !== null) {
          const label = match[1].trim();
          // Skip if it's just whitespace, separators, or common words
          if (label && label.length > 0 && !label.match(/^[,\s]+$/) && 
              !label.toLowerCase().match(/^(selected|unselected|none|some|between)$/i)) {
            matches.push({
              label: label,
              value: parseInt(match[2])
            });
          }
        }
        if (matches.length >= 3) {
          // Sort by value descending (highest score first for display)
          matches.sort((a, b) => b.value - a.value);
          return matches.map(m => ({
            label: m.label,
            value: m.label,
            numericValue: m.value
          }));
        }
      }
      
      // Try to extract from answerOptions object/array
      // This handles question-specific descriptive scales (e.g., AI training levels, competency measurement)
      // Use original answerOptions (not normalized) for object/array extraction since we need the structure
      if (answerOptions && typeof answerOptions === 'object' && !Array.isArray(answerOptions)) {
        if ('options' in answerOptions && Array.isArray(answerOptions.options)) {
          // If we have an array of descriptive options, use them
          // Try to infer numeric values from the order or scoring rules
          return answerOptions.options.map((opt: string, idx: number) => {
            // Check if option text contains scoring hints
            // Many custom scales go from worst (0) to best (4), so reverse if needed
            const reversedIdx = answerOptions.options.length - 1 - idx;
            return {
              label: opt,
              value: opt,
              numericValue: reversedIdx // Most custom scales: first option = 0, last = 4
            };
          });
        }
        // If object with keys (e.g., { "No training": 0, "Basic awareness": 1, ... })
        const entries = Object.entries(answerOptions);
        // Sort by value if they're numeric, otherwise by key order
        const sortedEntries = entries.sort((a, b) => {
          const valA = typeof a[1] === 'number' ? a[1] : parseFloat(a[1] as string) || 0;
          const valB = typeof b[1] === 'number' ? b[1] : parseFloat(b[1] as string) || 0;
          return valB - valA; // Sort descending (highest score first for display)
        });
        
        return sortedEntries.map(([key, val]) => ({
          label: key,
          value: key,
          numericValue: typeof val === 'number' ? val : parseFloat(val as string) || 0
        }));
      }
      // If string, try to parse descriptive labels (use normalized version)
      if (typeof answerOptionsForProcessing === 'string' && answerOptionsForProcessing.length > 0) {
        // Look for common patterns like "None = 0, Basic = 1, ..."
        const labelPattern = /([^=,]+)\s*=\s*(\d+)/g;
        const matches: Array<{label: string, value: number}> = [];
        let match;
        while ((match = labelPattern.exec(answerOptionsForProcessing)) !== null) {
          matches.push({
            label: match[1].trim(),
            value: parseInt(match[2])
          });
        }
        if (matches.length > 0) {
          // Sort by value descending
          matches.sort((a, b) => b.value - a.value);
          return matches.map(m => ({
            label: m.label,
            value: m.label,
            numericValue: m.value
          }));
        }
      }
      return [];

    default:
      // Fallback to never-always
      return [
        { label: 'Always', value: 'Always', numericValue: 4 },
        { label: 'Between Sometimes & Always', value: 'Between Sometimes & Always', numericValue: 3 },
        { label: 'Sometimes', value: 'Sometimes', numericValue: 2 },
        { label: 'Between Never & Sometimes', value: 'Between Never & Sometimes', numericValue: 1 },
        { label: 'Never', value: 'Never', numericValue: 0 }
      ];
  }
}

/**
 * Matches a saved response value to a scale option (handles both numeric and label formats)
 */
export function matchScaleValue(value: string, scaleOptions: ScaleOption[]): boolean {
  if (!value) return false;
  
  // Direct match
  const option = scaleOptions.find(opt => 
    opt.value === value || 
    opt.label === value ||
    opt.numericValue.toString() === value ||
    value === opt.numericValue.toString()
  );
  
  return !!option;
}

/**
 * Gets the scale option that matches a saved value
 */
export function getMatchingScaleOption(value: string, scaleOptions: ScaleOption[]): ScaleOption | undefined {
  if (!value) return undefined;
  
  return scaleOptions.find(opt => 
    opt.value === value || 
    opt.label === value ||
    opt.numericValue.toString() === value
  );
}

